import { useState } from 'react';
import { hoursToMilliseconds, intervalToDuration, isFuture, millisecondsToHours } from 'date-fns';
import AddressInput from '@/shared/components/AddressInput';
import { StatsPanel } from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import TokenInputField from '@/shared/components/TokenInputField';
import {
  type PendingRedemption,
  useRedemptionDelay,
  useRedemptionTax,
} from '@/shared/hooks/funding';
import useInterval from '@/shared/hooks/useInterval';
import InformationIcon from '@/shared/icons/flip-ui-kit/large/Information';
import CalendarIcon from '@/shared/icons/flip-ui-kit/small/Calendar';
import FeeIcon from '@/shared/icons/flip-ui-kit/small/Fee';
import GiveCoins from '@/shared/icons/flip-ui-kit/small/GiveCoins';
import Hourglass from '@/shared/icons/flip-ui-kit/small/Hourglass';
import { addToGoogleCalendar, pad } from '@/shared/utils/date';
import { chainflipAssetMap } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';

const ResumeInfo = () => (
  <span className="flex w-full items-center gap-x-1 text-14 text-cf-light-2">
    <InformationIcon /> You&apos;ll be able to resume this process if you exit the app.
  </span>
);

export const StartView = ({
  address,
  setAddress,
  redeemAmount,
  setRedeemAmount,
  flipBalance,
  redeemableBalance,
  type,
  tokenError,
  addressError,
  onClickMax,
}: {
  address: string;
  setAddress: (address: string) => void;
  redeemAmount: TokenAmount | undefined;
  setRedeemAmount: (amount: TokenAmount | undefined) => void;
  flipBalance: TokenAmount | null;
  redeemableBalance: TokenAmount | null;
  type: 'delegator' | 'validator';
  tokenError: string | undefined;
  addressError?: string | undefined;
  onClickMax?: VoidFunction;
}) => {
  const { data = null } = useRedemptionTax();
  const { data: delay = hoursToMilliseconds(48) } = useRedemptionDelay();

  const effectiveRedemptionTax =
    redeemAmount?.lt(flipBalance ?? TokenAmount.ZERO) ||
    redeemableBalance?.lt(flipBalance ?? TokenAmount.ZERO)
      ? TokenAmount.fromAsset(data, 'Flip')
      : TokenAmount.ZERO;

  const canRedeemAll = redeemableBalance?.eq(flipBalance) ?? false;

  let maxAmount = redeemableBalance;

  if (canRedeemAll && effectiveRedemptionTax?.gt(0)) {
    maxAmount = redeemableBalance?.sub(effectiveRedemptionTax) ?? null;
  }

  return (
    <>
      <div className="flex w-full flex-col items-center gap-6">
        {type === 'validator' && (
          <AddressInput
            fullWidth
            token={chainflipAssetMap.Flip}
            placeholder="Destination address"
            address={address}
            setAddress={setAddress}
            error={addressError}
          />
        )}
        <TokenInputField
          value={redeemAmount}
          onChange={setRedeemAmount}
          token={chainflipAssetMap.Flip}
          maxAmount={redeemableBalance ?? undefined}
          error={
            (tokenError ?? maxAmount?.lt(redeemAmount ?? TokenAmount.ZERO))
              ? 'Amount exceeds maximum'
              : undefined
          }
          onClickMax={onClickMax}
          acceptOverflowAmount
        />
      </div>
      <StatsPanel
        items={[
          {
            Icon: FeeIcon,
            flipAmount: effectiveRedemptionTax,
            label: 'Redemption fee',
            tooltipContent:
              'If you withdraw less than your total balance, the network charges this amount per redemption request to prevent spam.',
            loading: effectiveRedemptionTax === null,
          },
          {
            Icon: GiveCoins,
            flipAmount: flipBalance,
            label: 'Total balance',
            // tooltipContent: 'TODO(delegation)',
          },
          {
            Icon: GiveCoins,
            flipAmount: maxAmount,
            label: 'Available for withdrawal',
            // tooltipContent: 'TODO(delegation)',
          },
          {
            Icon: Hourglass,
            label: 'Funds available in',
            content: `${millisecondsToHours(delay)} hours`,
          },
        ]}
      />
    </>
  );
};

export const ThresholdSignatureView = () => (
  <div className="flex w-full flex-col items-center gap-y-6">
    <div className="flex items-center gap-x-2 py-4 text-14 text-cf-white">
      <LoadingSpinner />
      Waiting for execution
    </div>
    <ResumeInfo />
  </div>
);

export const FinalizeView = ({ redemption }: { redemption: PendingRedemption }) => {
  const [time, setTime] = useState(Date.now());

  useInterval(() => {
    setTime(Date.now());
  }, 1_000);

  if (!redemption) return null;

  let countdown: string[] | undefined;

  if (isFuture(redemption.expiryTime)) {
    const duration = intervalToDuration({
      start: time,
      end: isFuture(redemption.startTime) ? redemption.startTime : redemption.expiryTime,
    });

    countdown =
      `${pad(duration.days ?? 0)}:${pad(duration.hours ?? 0)}:${pad(duration.minutes ?? 0)}:${pad(duration.seconds ?? 0)}`.split(
        '',
      );
  }

  return (
    <div className="flex w-full flex-col items-center gap-y-6">
      <div className="flex flex-col items-center gap-y-4">
        {isFuture(redemption.startTime) ? (
          <Button
            type="secondary-standard"
            size="small"
            icon={<CalendarIcon className="mr-1" />}
            iconPos="left"
            className="flex items-center text-12"
            onClick={() =>
              addToGoogleCalendar(redemption.amount, redemption.startTime, redemption.expiryTime)
            }
          >
            Add to Google Calendar
          </Button>
        ) : (
          <Callout type="warning">
            <span className="font-aeonikBold">WARNING:</span> Your withdrawal expires in:
          </Callout>
        )}
        {countdown && (
          <>
            <div className="flex items-center gap-x-2 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 px-5 py-2 text-28 text-cf-white">
              {countdown.map((digit, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <span key={index}>{digit}</span>
              ))}
            </div>
            <div className="grid w-[300px] grid-cols-4 items-center text-14 text-cf-light-2">
              <div className="flex justify-center">DAYS</div>
              <div className="flex justify-center">HOURS</div>
              <div className="flex justify-center">MIN</div>
              <div className="flex justify-center">SEC</div>
            </div>
          </>
        )}
      </div>
      {isFuture(redemption.startTime) && <ResumeInfo />}
    </div>
  );
};
